import * as React from 'react'
import {
  useLoadingStore,
  selectLoading,
} from '@/modules/shared/stores/loadingStore'
import { LoaderIcon } from '@/app/features/icons/Loader'
import VehicleRow from './VehicleRow'
import { useVehicles } from '@/modules/shared/context/vehicles/VehiclesContext'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableEmpty from '@/modules/drivers/shared/tableItems/TableEmpty'
import { getVehiclesColumns } from '@/modules/vehicles/constants/tables'
import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import { useSideVehicleSelection } from '@/modules/vehicles/context/SideVehicleSelectionContext'
import { useSortableData } from '@/modules/drivers/hooks/useSortTable'

interface VehiclesTableProps {
  companyId: string
}

const VehiclesTable: React.FC<VehiclesTableProps> = ({ companyId }) => {
  const loading = useLoadingStore(selectLoading)
  const { t } = useLocalize()
  const {
    vehicles: filteredVehicles,
    search,
    isLoadingVehicles,
    hasMore,
    loadMore,
    isLoadingMore,
  } = useVehicles()

  const VehiclesColumns = React.useMemo(() => getVehiclesColumns(t), [t])

  const { sideCheckedIds } = useSideVehicleSelection()
  const displayedVehicles = React.useMemo(
    () =>
      sideCheckedIds.length > 0
        ? filteredVehicles.filter((v) => sideCheckedIds.includes(v.id))
        : [],
    [filteredVehicles, sideCheckedIds],
  )

  const { sortedData, sortConfig, handleHeaderClick } = useSortableData({
    data: displayedVehicles,
    tableColumns: VehiclesColumns,
    emptyValuesLast: true,
  })

  const sentinelRef = React.useRef<HTMLDivElement>(null)

  if (sideCheckedIds.length === 0) {
    return (
      <section className="tw-text-content-muted tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-gap-3">
        <p className="tw-text-sm tw-font-medium">
          Brak pojazdów w obszarze roboczym. Wybierz je z listy po lewej
        </p>
      </section>
    )
  }

  return (
    <section className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col">
      {loading || (isLoadingVehicles && filteredVehicles.length === 0) ? (
        <div className="tw-grid tw-place-items-center">
          <LoaderIcon className="tw-w-16 tw-animate-spin" />
        </div>
      ) : displayedVehicles.length > 0 ? (
        <div className="tw-h-full tw-overflow-y-auto">
          <table className="tw-relative tw-w-full tw-table-auto tw-border-spacing-x-2">
            <TableHeader
              opacity
              rounded={false}
              disabledCheckboxToggle
              columns={VehiclesColumns}
              sortConfig={sortConfig}
              sticky
              onHeaderClick={handleHeaderClick}
            />
            <tbody>
              {sortedData.map((item) => (
                <VehicleRow key={item.id} vehicle={item} />
              ))}
            </tbody>
          </table>
          {hasMore && (
            <div
              ref={sentinelRef}
              className="tw-flex tw-justify-center tw-py-4"
            >
              {isLoadingMore && (
                <LoaderIcon className="tw-w-8 tw-animate-spin" />
              )}
            </div>
          )}
        </div>
      ) : (
        <>
          <TableHeader
            opacity
            disabledCheckboxToggle
            columns={VehiclesColumns}
            sortConfig={sortConfig}
            onHeaderClick={handleHeaderClick}
          />
          <TableEmpty
            title={
              !search
                ? t('vehicleModule.no-vehicles')
                : t('vehicleModule.no-search-results')
            }
          />
        </>
      )}
    </section>
  )
}

export default VehiclesTable
